// What a transfer card SAYS (#6773, prototype mvdDraw `:3208-3225`): its head, its tag, the
// sentence a held or failed card shows, and its route. Pure of a state and a few lookups; every
// string goes through `t()`. A refusal's sentence is the ONE refusal renderer's
// (`moveRefusalText`, through `MovePage.textOf`) of the refusal still standing at the page
// clock, so a held card's countdown ticks with the page. A batch's lifecycle failure names both
// machines (`fleet.transfer.failed_all` / `failed_partial`): the note's "here" names nothing in
// a stack that floats over every card.

import type { Translator } from '@developerz/i18n';
import type { MoveRefusalWire } from './move-calls';
import { standingRefusal } from './move-copy';
import type { TransferCard, TransferState } from './transfer-stack';

/** The card's bold head. */
export function transferHead(card: TransferCard, s: TransferState, tr: Translator): string {
  switch (s.kind) {
    case 'moving':
    case 'retired':
      return tr(card.putBack ? 'fleet.transfer.head_putting_back' : 'fleet.transfer.head_moving');
    case 'complete':
      return tr(card.putBack ? 'fleet.transfer.head_put_back' : 'fleet.transfer.head_complete');
    case 'held':
      return tr('fleet.transfer.head_held');
    case 'failed':
      return tr('fleet.transfer.head_failed');
  }
}

/** The card's mono tag: "Step N of 3", "Done", "Limit" or "Refused". */
export function transferTag(s: TransferState, tr: Translator): string {
  switch (s.kind) {
    case 'moving':
    case 'retired':
      return tr('fleet.transfer.tag_step', { step: s.kind === 'moving' ? s.step + 1 : 1 });
    case 'complete':
      return tr('fleet.transfer.tag_done');
    case 'held':
      return tr('fleet.transfer.tag_limit');
    case 'failed':
      return tr('fleet.transfer.tag_refused');
  }
}

/** What a held or failed card's sentence reads beyond its state. */
export type TransferWhyContext = {
  /** The page's one refusal renderer (`MovePage.textOf`). */
  readonly textOf: (r: MoveRefusalWire) => string;
  /** The page clock, `Date.now` ms. */
  readonly nowMs: number;
  /** A machine's name by id. */
  readonly nameOf: (id: string) => string;
};

/** The held or failed card's sentence; null for any other state, or a wait that passed. */
export function transferWhy(
  card: TransferCard,
  s: TransferState,
  ctx: TransferWhyContext,
  tr: Translator,
): string | null {
  if (s.kind === 'held') {
    const r = standingRefusal(s.refusal, ctx.nowMs);
    return r === null ? null : ctx.textOf(r);
  }
  if (s.kind !== 'failed') return null;
  if (s.refusal !== null) return ctx.textOf(standingRefusal(s.refusal, ctx.nowMs) ?? s.refusal);
  if (s.batch === null || card.toId === null) return tr('fleet.move.move_failed');
  const names = { to: ctx.nameOf(card.toId), from: ctx.nameOf(card.fromId) };
  return s.batch.moved === 0
    ? tr('fleet.transfer.failed_all', { what: card.name, ...names })
    : tr('fleet.transfer.failed_partial', { moved: s.batch.moved, total: s.batch.total, ...names });
}

/** The progress bar's fill for a state: 18 / 52 / 84 % for the steps, full when complete. */
export function transferProgress(s: TransferState): number | null {
  if (s.kind === 'complete') return 1;
  if (s.kind === 'moving') return [0.18, 0.52, 0.84][s.step] ?? 0.84;
  return null;
}
